import React from 'react'
import styled, { keyframes } from 'styled-components'
import { Spin } from 'antd'
import conferLogo from '../../assets/images/Confer Logo.svg'

interface LoadingSplashProps {
  message?: string
  showLogo?: boolean
  size?: 'small' | 'default' | 'large'
  fullScreen?: boolean
  backgroundColor?: string
  textColor?: string
}

const LoadingSplash: React.FC<LoadingSplashProps> = ({
  message = 'Loading...',
  showLogo = true,
  size = 'default',
  fullScreen = true,
  backgroundColor,
  textColor,
}) => {
  return (
    <SplashContainer fullScreen={fullScreen} backgroundColor={backgroundColor}>
      <ContentWrapper>
        {showLogo && (
          <LogoContainer>
            <Logo src={conferLogo} alt="Confer" />
          </LogoContainer>
        )}

        <SpinnerContainer>
          <StyledSpin size={size} />
        </SpinnerContainer>

        <Message textColor={textColor}>{message}</Message>

        <DotsContainer>
          <Dot delay="0s" />
          <Dot delay="0.2s" />
          <Dot delay="0.4s" />
        </DotsContainer>
      </ContentWrapper>
    </SplashContainer>
  )
}

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
`

const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
`

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`

// Styled Components
interface SplashContainerProps {
  fullScreen: boolean
  backgroundColor?: string
}

const SplashContainer = styled.div<SplashContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ backgroundColor }) =>
    backgroundColor || 'linear-gradient(135deg,rgb(169, 169, 169) 0%,rgb(76, 76, 76) 100%)'};
  color: white;
  animation: ${fadeIn} 0.6s ease-out;

  ${({ fullScreen }) =>
    fullScreen
      ? `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 9999;
      `
      : `
        width: 100%;
        height: 100%;
        min-height: 300px;
      `}
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  text-align: center;
  animation: ${float} 3s ease-in-out infinite;
`

const LogoContainer = styled.div`
  animation: ${pulse} 2s ease-in-out infinite;
`

const Logo = styled.img`
  height: 80px;
  width: auto;
  filter: brightness(0) invert(1);
`

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledSpin = styled(Spin)`
  .ant-spin-dot-item {
    background-color: white !important;
  }

  &.ant-spin-lg .ant-spin-dot-item {
    width: 12px !important;
    height: 12px !important;
  }

  &.ant-spin-sm .ant-spin-dot-item {
    width: 6px !important;
    height: 6px !important;
  }
`

interface MessageProps {
  textColor?: string
}

const Message = styled.div<MessageProps>`
  font-size: 1.2rem;
  font-weight: 500;
  color: ${({ textColor }) => textColor || 'white'};
  margin: 0;
  letter-spacing: 0.5px;
`

const DotsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
`

const Dot = styled.div<{ delay: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: white;
  animation: ${bounce} 1.4s ease-in-out infinite both;
  animation-delay: ${({ delay }) => delay};
`

export default LoadingSplash
